import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import NavBar from "../components/NavBar";
import FooterPage from "../components/FooterPage";
import "../styles/site.css";

const BUSINESS_NAME = "Indian Odyssey Trips";
const ADDRESS = "3-501, Near Ramarao Colony, Main Road, Sarpavaram, Kakinada Rural, Kakinada, Andhra Pradesh 533005, India";
const PHONE = "+919121943442";
const PHONE_LABEL = "+91 91219 43442";
const EMAIL = "indianodysseyofficial@gmail.com";
const GRIEVANCE_OFFICER = "Veerendhar Reddy";
const LAST_UPDATED = "4 November 2026";
const TERMS_FILE = "/policies/Indian_Odyssey_Trips_Travel_Terms_and_Conditions.pdf";

const PrivacyPolicyPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = `Privacy Policy - ${BUSINESS_NAME}`;
  }, []);

  // Links such as /privacy-policy#data-deletion should land on that section.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <div>
      <NavBar />
      <main className="site-policy">
        <div className="container site-policy-body">
          <h1>Privacy Policy</h1>
          <p className="site-policy-updated">Last updated: {LAST_UPDATED}</p>

          <p>
            This policy explains what personal information {BUSINESS_NAME} ("we", "us") collects when you
            enquire about, book or take a trip with us, how we use it, who we share it with, and the choices
            you have. It applies to our website www.iotrips.in, our WhatsApp numbers, phone calls, email and
            the trip pages we send you.
          </p>

          <section id="who-we-are">
            <h2>1. Who we are</h2>
            <p>
              {BUSINESS_NAME} is a travel services business registered in India (Udyam registration
              UDYAM-AP-17-0054359).
            </p>
            <ul>
              <li>Address: {ADDRESS}</li>
              <li>Phone and WhatsApp: <a href={`tel:${PHONE}`}>{PHONE_LABEL}</a></li>
              <li>Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
            </ul>
          </section>

          <section id="what-we-collect">
            <h2>2. Information we collect</h2>
            <ul>
              <li>
                <strong>Enquiry details:</strong> your name, phone and WhatsApp number, email, preferred
                language, destination, travel dates, number of travellers and pickup point.
              </li>
              <li>
                <strong>Messages and calls:</strong> the messages, photos, videos, voice notes and documents you
                send us on WhatsApp, and our replies. Calls with our team may be recorded for quality and
                record keeping.
              </li>
              <li>
                <strong>Booking details:</strong> traveller names, room and vehicle needs, payment amounts,
                payment screenshots and receipts. We do not collect or store your card or bank login details.
              </li>
              <li>
                <strong>ID documents:</strong> a government ID (such as Aadhaar) that you upload for hotel
                check-in or permits.
              </li>
              <li>
                <strong>Location:</strong> with your permission, your location when you accept the trip policy
                and the pickup point you set on your trip page. During the trip we show you the location of your
                vehicle.
              </li>
              <li>
                <strong>Emergency contacts</strong> that you add for your trip.
              </li>
              <li>
                <strong>Website data:</strong> your login details if you sign in, your wishlist, and basic
                technical data such as browser and device type, stored through cookies or similar browser
                storage needed for the website to work.
              </li>
            </ul>
          </section>

          <section id="how-we-use">
            <h2>3. How we use your information</h2>
            <ul>
              <li>To answer your enquiry and send quotations, itineraries and room photos.</li>
              <li>To book and run your trip: hotels, vehicles, drivers, activities, permits and pickup.</li>
              <li>To collect payments and send payment reminders and receipts.</li>
              <li>To keep you informed and safe during the trip, including trip tracking and emergency support.</li>
              <li>To handle complaints, refunds and customer support.</li>
              <li>To keep business, tax and legal records.</li>
              <li>To improve our services and train our team.</li>
            </ul>
            <p>
              We may use automated tools, including AI assistants, to help read and reply to your messages
              quickly. Prices and booking details always come from our own systems, and our team can step in
              at any time.
            </p>
            <p>We do not sell your personal information.</p>
          </section>

          <section id="whatsapp">
            <h2>4. WhatsApp messages</h2>
            <p>
              When you message us on WhatsApp, your messages reach us through the WhatsApp Business Platform
              provided by Meta. We store a copy of our conversations, including photos, videos, voice notes and
              documents, in our own secure systems so that our team can see the full history of your enquiry
              and booking. WhatsApp's own privacy policy applies to your use of WhatsApp.
            </p>
          </section>

          <section id="sharing">
            <h2>5. Who we share it with</h2>
            <ul>
              <li>
                <strong>Travel partners:</strong> hotels, transport operators, drivers and activity providers
                receive only what they need to serve you, such as names, guest count, contact number, pickup
                details and ID for check-in.
              </li>
              <li>
                <strong>Service providers</strong> that run our systems for us: Meta (WhatsApp), Amazon Web
                Services (hosting and file storage), our telephone call provider, SMS and email providers, our
                website login provider and Google Maps. Some of these providers may process data outside India.
              </li>
              <li>
                <strong>Authorities,</strong> when the law requires it or to protect the safety of travellers.
              </li>
            </ul>
          </section>

          <section id="security">
            <h2>6. How we protect and keep it</h2>
            <ul>
              <li>
                Your information is stored on secure cloud servers. ID documents and chat media are kept in
                private storage that only authorised staff can open.
              </li>
              <li>Staff access is limited to what each role needs.</li>
              <li>
                We keep information only as long as needed for the purposes above, and longer where the law
                requires it (for example tax and accounting records).
              </li>
            </ul>
          </section>

          <section id="your-rights">
            <h2>7. Your rights</h2>
            <p>
              Under India's Digital Personal Data Protection Act, 2023 you can ask us to:
            </p>
            <ul>
              <li>tell you what personal information we hold about you and how we use it;</li>
              <li>correct or update it;</li>
              <li>delete it (see the next section);</li>
              <li>stop using it where you gave us consent, for example for promotional messages.</li>
            </ul>
            <p>
              You can also nominate another person to use these rights for you. To use any of these rights,
              contact us using the details below.
            </p>
          </section>

          <section id="data-deletion">
            <h2>8. How to delete your data</h2>
            <ol>
              <li>
                Email <a href={`mailto:${EMAIL}`}>{EMAIL}</a> with the subject "Delete my data", or send
                "Delete my data" on WhatsApp to <a href={`tel:${PHONE}`}>{PHONE_LABEL}</a>.
              </li>
              <li>Include the phone number you used with us, so we can find your records.</li>
              <li>We may ask you to confirm the request from that same number.</li>
            </ol>
            <p>
              We will delete your enquiry details, messages, call recordings, ID documents and location data
              within 30 days and confirm when it is done. We keep only what the law requires, such as invoices
              and payment records for completed bookings.
            </p>
            <p>
              Deleting a chat in WhatsApp on your phone does not delete our copy. Please send us a request as
              above.
            </p>
          </section>

          <section id="children">
            <h2>9. Children</h2>
            <p>
              Our services are booked by adults. Details of children travelling with a family are given to us
              by their parent or guardian and used only to arrange the trip.
            </p>
          </section>

          <section id="grievance-officer">
            <h2>10. Grievance Officer</h2>
            <p>For questions or complaints about your personal information, contact our Grievance Officer:</p>
            <ul>
              <li>Name: {GRIEVANCE_OFFICER}</li>
              <li>Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
              <li>Phone: <a href={`tel:${PHONE}`}>{PHONE_LABEL}</a></li>
              <li>Address: {ADDRESS}</li>
            </ul>
            <p>We will reply within 30 days.</p>
          </section>

          <section id="changes">
            <h2>11. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. The latest version is always on this page, with the
              date it was last updated. Our{" "}
              <a href={TERMS_FILE} target="_blank" rel="noreferrer">Travel Terms &amp; Conditions</a> cover
              bookings, payments and cancellations.
            </p>
          </section>
        </div>
      </main>
      <FooterPage />
    </div>
  );
};

export default PrivacyPolicyPage;
